/**
 * DENTASTIC storefront behaviours — a port of the React primitives in
 * components/storefront/primitives.tsx, Header.tsx, FlossTest.tsx and
 * BundleSelector.tsx. Vanilla, progressive: every one of these leaves the
 * page fully usable when it does not run.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Scroll reveal ─────────────────────────────────────────────────────
     The hidden state is applied here, not in CSS, so content is never stuck
     invisible if this script fails to load. */
  function reveal() {
    if (reduce || !('IntersectionObserver' in window)) return;
    var els = document.querySelectorAll('.dnt-reveal');
    if (!els.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.style.opacity = '1';
        e.target.style.transform = 'none';
        io.unobserve(e.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -6% 0px' });
    els.forEach(function (el) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(16px)';
      io.observe(el);
    });
  }

  /* ── Announcement height → sticky offset for #header-group ───────────── */
  function announce() {
    var strip = document.querySelector('.dnt-announce');
    var group = document.getElementById('header-group');
    if (!strip || !group) return;
    function set() { group.style.setProperty('--dnt-announce-h', strip.offsetHeight + 'px'); }
    set();
    if ('ResizeObserver' in window) new ResizeObserver(set).observe(strip);
  }

  /* ── Mobile nav ──────────────────────────────────────────────────────── */
  function header() {
    var btn = document.querySelector('[data-dnt-nav-toggle]');
    var drawer = document.getElementById('dnt-mobile-nav');
    if (!btn || !drawer) return;
    function set(open) {
      drawer.hidden = !open;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) btn.setAttribute('aria-controls', 'dnt-mobile-nav');
      else btn.removeAttribute('aria-controls');
    }
    btn.addEventListener('click', function () { set(drawer.hidden); });
    drawer.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', function () { set(false); }); });
    var mq = window.matchMedia('(min-width:1001px)');
    var onChange = function (e) { if (e.matches) set(false); };
    onChange(mq);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
  }

  /* ── Single-open accordion (aria-expanded / aria-controls) ───────────── */
  function accordions() {
    document.querySelectorAll('[data-dnt-accordion]').forEach(function (root) {
      var btns = root.querySelectorAll('.dnt-acc-btn');
      btns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var wasOpen = btn.getAttribute('aria-expanded') === 'true';
          btns.forEach(function (b) {
            var panel = document.getElementById(b.getAttribute('aria-controls'));
            var open = b === btn && !wasOpen;
            b.setAttribute('aria-expanded', open ? 'true' : 'false');
            b.querySelector('.dnt-acc-glyph').textContent = open ? '–' : '+';
            if (panel) panel.hidden = !open;
          });
        });
      });
    });
  }

  /* ── Bundle radio group (roving tabindex) + gallery frames ───────────── */
  function radios() {
    document.querySelectorAll('[data-dnt-selector]').forEach(function (root) {
      var rows = Array.prototype.slice.call(root.querySelectorAll('.dnt-radio'));
      if (!rows.length) return;
      var frames = Array.prototype.slice.call(root.querySelectorAll('[data-dnt-stage] img'));
      var caption = root.querySelector('[data-dnt-stage-caption]');
      var buy = root.querySelector('[data-dnt-buy]');
      var buyLabel = root.querySelector('[data-dnt-buy-label]');
      var addForm = root.querySelector('[data-dnt-add-form]');
      var addInput = addForm && addForm.querySelector('input[name="id"]');
      var thumbs = Array.prototype.slice.call(root.querySelectorAll('.dnt-thumb'));

      function showFrame(img, fallbackCaption) {
        frames.forEach(function (f) {
          var on = f === img;
          f.classList.toggle('is-active', on);
          f.setAttribute('aria-hidden', on ? 'false' : 'true');
        });
        thumbs.forEach(function (t) {
          var on = img && t.getAttribute('data-frame') === img.getAttribute('data-frame');
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        if (caption) caption.textContent = (img && img.getAttribute('data-caption')) || fallbackCaption || '';
      }

      function select(row) {
        rows.forEach(function (r) {
          var sel = r === row;
          r.setAttribute('aria-checked', sel ? 'true' : 'false');
          r.tabIndex = sel ? 0 : -1;
        });
        var key = row.getAttribute('data-value');
        var img = frames.filter(function (f) { return f.getAttribute('data-value') === key; })[0];
        showFrame(img, row.getAttribute('data-per-tube-label'));
        if (buy) buy.setAttribute('href', row.getAttribute('data-permalink'));
        if (buyLabel) buyLabel.textContent = row.getAttribute('data-buy-label');
        if (addInput) addInput.value = row.getAttribute('data-variant');
        root.dispatchEvent(new CustomEvent('dnt:tier', { detail: { tubes: key, row: row }, bubbles: true }));
      }

      rows.forEach(function (row, i) {
        row.addEventListener('click', function () { select(row); });
        row.addEventListener('keydown', function (e) {
          var keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
          if (keys.indexOf(e.key) === -1) return;
          e.preventDefault();
          var last = rows.length - 1, next = i;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = last;
          select(rows[next]);
          rows[next].focus();
        });
      });

      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () {
          var img = frames.filter(function (f) { return f.getAttribute('data-frame') === t.getAttribute('data-frame'); })[0];
          showFrame(img);
        });
        t.addEventListener('keydown', function (e) {
          if (['ArrowLeft', 'ArrowRight'].indexOf(e.key) === -1) return;
          e.preventDefault();
          var next = e.key === 'ArrowRight' ? (i === thumbs.length - 1 ? 0 : i + 1) : (i === 0 ? thumbs.length - 1 : i - 1);
          thumbs[next].click(); thumbs[next].focus();
        });
      });

      // Initial frame: the checked tier (PDP preselects the current product's tier).
      var checked = rows.filter(function (r) { return r.getAttribute('aria-checked') === 'true'; })[0];
      if (checked && frames.length) {
        var key0 = checked.getAttribute('data-value');
        var img0 = frames.filter(function (f) { return f.getAttribute('data-value') === key0; })[0];
        if (img0 && root.hasAttribute('data-dnt-pdp')) showFrame(img0, checked.getAttribute('data-per-tube-label'));
      }
    });
  }

  /* ── Sticky mobile buy bar ──────────────────────────────────────────── */
  function buybar() {
    var bar = document.querySelector('[data-dnt-buybar]');
    if (!bar || !('IntersectionObserver' in window)) return;
    var anchor = document.querySelector('[data-buybar-anchor]');
    var footer = document.querySelector('[data-buybar-stop]');
    var past = false, atFooter = false;
    function paint() { var on = past && !atFooter; bar.setAttribute('data-shown', on ? 'true' : 'false'); bar.setAttribute('aria-hidden', on ? 'false' : 'true'); }
    if (anchor) new IntersectionObserver(function (es) { var e = es[0]; past = !e.isIntersecting && e.boundingClientRect.top < 0; paint(); }, { threshold: 0 }).observe(anchor);
    if (footer) new IntersectionObserver(function (es) { atFooter = es[0].isIntersecting; paint(); }, { threshold: 0 }).observe(footer);
    document.addEventListener('dnt:tier', function (e) {
      bar.querySelectorAll('[data-tubes]').forEach(function (row) { row.hidden = row.getAttribute('data-tubes') !== String(e.detail.tubes); });
    });
  }

  /* ── Reading progress + sticky CTA (articles, advertorials) ──────────── */
  function reading() {
    var bar = document.querySelector('[data-dnt-progress]');
    var sticky = document.querySelector('[data-dnt-sticky-cta]');
    if (!bar && !sticky) return;
    var fill = bar && bar.querySelector('span');
    function onScroll() {
      var el = document.documentElement;
      var total = el.scrollHeight - el.clientHeight;
      var p = total > 0 ? (window.scrollY || el.scrollTop) / total : 0;
      if (fill) { fill.style.width = (p * 100) + '%'; bar.setAttribute('aria-valuenow', String(Math.round(p * 100))); }
      if (sticky) { var on = p > 0.5; sticky.setAttribute('data-shown', on ? 'true' : 'false'); sticky.setAttribute('aria-hidden', on ? 'false' : 'true'); }
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ── Klaviyo Client API subscribe (shared by capture forms + reveal) ──── */
  var KLAVIYO_URL = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2026-07-15';
  function klaviyoSubscribe(company, list, email, source, properties) {
    var attrs = { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } };
    if (properties) attrs.properties = properties;
    var body = { data: { type: 'subscription', attributes: { custom_source: source, profile: { data: { type: 'profile', attributes: attrs } } }, relationships: { list: { data: { type: 'list', id: list } } } } };
    return fetch(KLAVIYO_URL + '?company_id=' + encodeURIComponent(company), {
      method: 'POST', headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION }, body: JSON.stringify(body)
    });
  }
  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

  /* ── Consent gate (Shopify Customer Privacy API) ──────────────────────
     The Klaviyo subscribe POST is a browser-originated marketing request, so
     it follows the same gate as the trackers (headless: useTrackersEnabled).
     Shopify decides per region: HK/US consent not required → allowed by
     default; DE/GB required → allowed only after the banner's accept.
     Resolves to true/false; never throws. */
  function marketingAllowed() {
    return new Promise(function (resolve) {
      function read() {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp) return resolve(true);
        try { resolve(cp.marketingAllowed() !== false); } catch (e) { resolve(true); }
      }
      if (window.Shopify && window.Shopify.customerPrivacy) return read();
      if (window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) { if (err) return resolve(true); read(); });
      } else resolve(true);
    });
  }
  function klaviyoIdentify(email, properties) {
    if (!(window.klaviyo && window.klaviyo.identify)) return;
    var p = { $email: email };
    if (properties) for (var k in properties) p[k] = properties[k];
    try { window.klaviyo.identify(p); } catch (err) {}
  }

  function capture() {
    document.querySelectorAll('[data-dnt-capture]').forEach(function (root) {
      var form = root.querySelector('[data-dnt-capture-form]');
      var status = root.querySelector('[data-dnt-capture-status]');
      var done = root.querySelector('[data-dnt-capture-done]');
      var btn = form.querySelector('button[type=submit]');
      var t = window.dntT || {};
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var email = form.email.value.trim();
        if (form.website && form.website.value) { form.hidden = true; done.hidden = false; return; }
        if (!validEmail(email)) { status.textContent = t.needEmail; return; }
        if (!form.consent.checked) { status.textContent = t.needConsent; return; }
        status.textContent = ''; btn.disabled = true; var label = btn.textContent; btn.textContent = t.sending;
        marketingAllowed()
          .then(function (allowed) {
            if (!allowed) throw new Error(t.needCookies);
            return klaviyoSubscribe(root.getAttribute('data-company'), root.getAttribute('data-list'), email, root.getAttribute('data-source'));
          })
          .then(function (r) {
            if (r.status === 429) throw new Error(t.rate);
            if (!r.ok && r.status !== 202) throw new Error(t.error);
            form.hidden = true; done.hidden = false;
            klaviyoIdentify(email);
          })
          .catch(function (err) { status.textContent = err.message || t.error; })
          .then(function () { btn.disabled = false; btn.textContent = label; });
      });
    });
  }

  /* ── Pick-a-card discount reveal ─────────────────────────────────────
     Port of components/marketing/DiscountReveal.tsx (production ff4bf5c +
     08bdaae + the session-63 browser-side subscribe). Two draw modes:
       · API  — data-api set: POST {email, consent, source:'reveal', nonce}
                to the surviving Vercel route (admin.trydenttastic.com); the
                server draws the tier by salted nonce, rate-limits by IP and
                returns {tier:{id,label,percent,code}}. The page never holds
                a code; the browser then subscribes + identifies with the
                tier properties, exactly like the headless site since 8be179c.
       · local — data-api empty: the tier is drawn here by the settings'
                weights (the honest-draw guarantee is lost: weights are
                editable in devtools; the rate limit is Klaviyo's own).
     Both modes: the Klaviyo POST is browser-originated and gated on
     Shopify's marketingAllowed(); identify carries discount_tier too because
     double opt-in holds the subscribe request's properties until the click. */
  function revealPopup() {
    var root = document.querySelector('[data-dnt-reveal]');
    if (!root) return;
    var ls; try { ls = window.localStorage; } catch (e) { ls = null; }
    var ss; try { ss = window.sessionStorage; } catch (e) { ss = null; }
    var q = new URLSearchParams(location.search);
    if (q.has('discount') || q.has('discount_code') || q.has('_kx')) return;
    if (q.get('utm_source') === 'klaviyo') return;
    if (ls && Number(ls.getItem('dt_reveal_dismissed_until') || 0) > Date.now()) return;
    if (ls && Number(ls.getItem('dt_reveal_shown_until') || 0) > Date.now()) return;
    if (ss && ss.getItem('dt_kl_id_kx')) return;
    var tiers = JSON.parse(root.querySelector('[data-dnt-reveal-tiers]').textContent || '[]');
    var mode = root.getAttribute('data-mode');
    var api = root.getAttribute('data-api') || '';
    var title = root.querySelector('[data-dnt-reveal-title]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('.dnt-card-btn'));
    var form = root.querySelector('[data-dnt-reveal-form]');
    var status = root.querySelector('[data-dnt-reveal-status]');
    var doneEl = root.querySelector('[data-dnt-reveal-done]');
    var t = window.dntT || {};
    var drawn = null, shown = false, allowed = null;
    var DAY = 86400000;
    var panel = root.querySelector('.dnt-popup-panel') || root;
    var prevFocus = null, userScrolled = false, deferred = false;

    /* The cart drawer (<theme-drawer id="cart-drawer">, native <dialog>) sets
       [open] on itself while it is showing; never stack the popup over it. */
    function cartDrawerOpen() {
      var d = document.getElementById('cart-drawer');
      return !!(d && (d.hasAttribute('open') || d.querySelector('dialog[open]')));
    }
    /* Minimal focus trap (A11Y-1): aria-modal alone does not keep Tab inside. */
    function focusables() {
      var sel = 'a[href],button:not([disabled]),input:not([disabled]):not([tabindex="-1"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
      return Array.prototype.filter.call(panel.querySelectorAll(sel), function (el) { return el.getClientRects().length > 0; });
    }

    function localDraw() {
      var total = tiers.reduce(function (a, x) { return a + (Number(x.weight) || 0); }, 0);
      var r = Math.random() * total;
      for (var i = 0; i < tiers.length; i++) { r -= Number(tiers[i].weight) || 0; if (r <= 0) return tiers[i]; }
      return tiers[tiers.length - 1];
    }
    function nonce() {
      var a = new Uint8Array(16);
      if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(a); else for (var i = 0; i < 16; i++) a[i] = Math.floor(Math.random() * 256);
      return Array.prototype.map.call(a, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
    }
    function apiDraw(email) {
      return fetch(api, {
        method: 'POST', credentials: 'omit', mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, consent: true, source: 'reveal', website: '', nonce: nonce() })
      }).then(function (r) {
        if (r.status === 429) throw new Error(t.rate);
        if (!r.ok && r.status !== 202) throw new Error(t.error);
        return r.json();
      }).then(function (d) {
        if (!d || d.subscribe === false) return { subscribe: false };
        var tier = d.tier || {};
        var pct = tier.percent != null ? (String(tier.percent).indexOf('%') > -1 ? String(tier.percent) : tier.percent + '%') : '';
        return { subscribe: true, tier: { id: tier.id, percent: pct, code: mode === 'page' ? (tier.code || '') : '' } };
      });
    }
    function open() {
      if (shown) return; shown = true;
      prevFocus = document.activeElement;
      root.hidden = false;
      document.documentElement.setAttribute('scroll-lock', '');
      if (ls) ls.setItem('dt_reveal_shown_until', String(Date.now() + 30 * DAY));
      var first = root.querySelector('input[type=email]'); if (first) first.focus();
    }
    function close(days) {
      root.hidden = true;
      document.documentElement.removeAttribute('scroll-lock');
      if (ls) ls.setItem('dt_reveal_dismissed_until', String(Date.now() + (days || 30) * DAY));
      var back = prevFocus && document.contains(prevFocus) && prevFocus !== document.body ? prevFocus : document.body;
      try { back.focus(); } catch (e) {}
      prevFocus = null;
    }
    root.querySelectorAll('[data-dnt-reveal-close]').forEach(function (b) { b.addEventListener('click', function () { close(30); }); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !root.hidden) close(30); });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || root.hidden) return;
      var f = focusables();
      if (!f.length) { e.preventDefault(); return; }
      var first = f[0], last = f[f.length - 1], cur = document.activeElement;
      if (e.shiftKey) { if (cur === first || !panel.contains(cur)) { e.preventDefault(); last.focus(); } }
      else if (cur === last || !panel.contains(cur)) { e.preventDefault(); first.focus(); }
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = form.email.value.trim();
      if (form.website && form.website.value) { close(30); return; }
      if (!validEmail(email)) { status.textContent = t.needEmail; return; }
      var btn = form.querySelector('button[type=submit]'); btn.disabled = true; var label = btn.textContent; btn.textContent = t.sending;
      status.textContent = '';
      var company = root.getAttribute('data-company'), list = root.getAttribute('data-list');
      marketingAllowed()
        .then(function (ok) {
          if (!ok) throw new Error(t.needCookies);
          if (api) return apiDraw(email).catch(function (err) {
            if (err && (err.message === t.rate)) throw err;
            /* route unreachable (CORS/DNS/5xx): fall back to the local draw so a
               visitor with a valid email is never stranded — logged, not hidden */
            try { console.warn('[dnt] reveal API unavailable, local draw', err); } catch (x) {}
            return { subscribe: true, tier: localDraw() };
          });
          return { subscribe: true, tier: localDraw() };
        })
        .then(function (d) {
          if (!d.subscribe) { close(30); return null; }
          drawn = d.tier;
          var props = { discount_tier: drawn.id, discount_percent: drawn.percent };
          return klaviyoSubscribe(company, list, email, 'Pick-a-card discount signup', props).then(function (r) {
            if (r.status === 429) throw new Error(t.rate);
            if (!r.ok && r.status !== 202) throw new Error(t.error);
            form.hidden = true; title.textContent = title.getAttribute('data-t-pick');
            cards.forEach(function (c) { c.disabled = false; c.classList.add('is-live'); });
            klaviyoIdentify(email, props);
          });
        })
        .catch(function (err) { status.textContent = (err && err.message) || t.error; btn.disabled = false; btn.textContent = label; });
    });

    cards.forEach(function (c) {
      c.addEventListener('click', function () {
        if (!c.classList.contains('is-live') || !drawn) return;
        cards.forEach(function (o) { o.classList.remove('is-live'); o.disabled = true; if (o !== c) o.classList.add('is-dim'); });
        c.querySelector('[data-card-percent]').textContent = drawn.percent;
        c.classList.add('is-flipped');
        c.setAttribute('aria-label', (t.yourDiscount || '{{ percent }}').replace('{{ percent }}', drawn.percent));
        title.textContent = title.getAttribute(mode === 'page' ? 'data-t-done-page' : 'data-t-done-email');
        doneEl.hidden = false;
        if (mode === 'page' && drawn.code) {
          var wrap = root.querySelector('[data-dnt-reveal-code-wrap]'); wrap.hidden = false;
          root.querySelector('[data-dnt-reveal-code]').textContent = drawn.code;
          var copy = root.querySelector('[data-dnt-reveal-copy]');
          copy.addEventListener('click', function () { try { navigator.clipboard.writeText(drawn.code); copy.textContent = copy.getAttribute('data-t-copied'); setTimeout(function () { copy.textContent = copy.getAttribute('data-t-copy'); }, 2000); } catch (err) {} });
        }
        if (window.gtag) try { window.gtag('event', 'discount_reveal', { tier: drawn.id }); } catch (err) {}
        if (window.klaviyo && window.klaviyo.push) try { window.klaviyo.push(['track', 'Discount Revealed', { tier: drawn.id, percent: drawn.percent }]); } catch (err) {}
        if (ls) ls.setItem('dt_reveal_dismissed_until', String(Date.now() + 30 * DAY));
      });
    });

    /* Triggers as on production: dwell (section setting, 12 s default),
       45 % scroll depth, exit intent on fine pointers. The popup never opens
       for a visitor whose region requires consent they have not given. */
    function tryOpen() {
      if (shown) return;
      if (cartDrawerOpen()) {
        /* defer while the cart drawer is up: re-check every 2 s, open once it closes */
        if (!deferred) { deferred = true; setTimeout(function () { deferred = false; tryOpen(); }, 2000); }
        return;
      }
      if (allowed === null) { marketingAllowed().then(function (ok) { allowed = ok; if (ok) tryOpen(); }); return; }
      if (allowed) open();
    }
    var delay = Number(root.getAttribute('data-delay') || 12) * 1000;
    setTimeout(tryOpen, delay);
    /* The 45 % trigger is armed only after a real user gesture, so the page's
       own scrollIntoView (legacyAnchor: /about → /#founder) cannot fire it. */
    ['wheel', 'touchstart', 'keydown'].forEach(function (ev) {
      window.addEventListener(ev, function (e) { if (e.isTrusted) userScrolled = true; }, { passive: true });
    });
    window.addEventListener('scroll', function onS() {
      if (!userScrolled) return;
      var el = document.documentElement, total = el.scrollHeight - el.clientHeight;
      if (total > 0 && (window.scrollY || el.scrollTop) / total >= 0.45) { window.removeEventListener('scroll', onS); tryOpen(); }
    }, { passive: true });
    if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
      document.addEventListener('mouseleave', function (e) { if (e.clientY <= 0) tryOpen(); });
    }
  }

  /* ── Floss test: 60 s wall-clock timer ───────────────────────────────── */
  function floss() {
    document.querySelectorAll('[data-dnt-floss]').forEach(function (root) {
      var DURATION = 60, STEP_AT = [0, 15, 45];
      var hexes = root.querySelectorAll('.dnt-hex');
      var bar = root.querySelector('.dnt-progress');
      var fill = bar && bar.querySelector('span');
      var begin = root.querySelector('[data-dnt-floss-begin]');
      var reset = root.querySelector('[data-dnt-floss-reset]');
      var status = root.querySelector('[data-dnt-floss-status]');
      var labels = {
        idle: begin.getAttribute('data-label-idle'),
        remaining: begin.getAttribute('data-label-remaining'),
        done: begin.getAttribute('data-label-done'),
        announce: status ? status.getAttribute('data-label-announce') : ''
      };
      var started = null, timer = null;

      function paint(elapsed) {
        hexes.forEach(function (h, i) { h.classList.toggle('is-filled', started !== null && elapsed >= STEP_AT[i]); });
        if (fill) fill.style.width = (Math.min(DURATION, elapsed) / DURATION * 100) + '%';
        if (bar) bar.setAttribute('aria-valuenow', String(Math.floor(elapsed)));
        var done = elapsed >= DURATION;
        begin.textContent = started === null ? labels.idle : done ? labels.done : labels.remaining.replace('{{ seconds }}', String(Math.ceil(DURATION - elapsed)));
        begin.disabled = started !== null && !done;
        if (reset) reset.hidden = started === null;
        if (status) status.textContent = done ? labels.announce : '';
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      begin.addEventListener('click', function () {
        if (started !== null && timer) return;
        started = Date.now();
        timer = setInterval(function () {
          var el = Math.min(DURATION, (Date.now() - started) / 1000);
          paint(el);
          if (el >= DURATION) stop();
        }, 100);
        paint(0);
      });
      if (reset) reset.addEventListener('click', function () { stop(); started = null; paint(0); });
      paint(0);
    });
  }

  /* ── Legacy-route landing: /about → /?dnt=founder, /ingredients → /?dnt=ingredients
     Shopify URL redirects cannot carry a #fragment reliably, so the redirect
     CSV targets a query string and the page scrolls itself to the section,
     then rewrites the address to the plain anchor. */
  function legacyAnchor() {
    var m = /[?&]dnt=([a-z-]+)/.exec(location.search);
    if (!m) return;
    var el = document.getElementById(m[1]);
    if (!el) return;
    try { history.replaceState(null, '', location.pathname + '#' + m[1]); } catch (e) {}
    el.scrollIntoView({ block: 'start' });
  }
  /* ── Forms → surviving Vercel API (session 66) ─────────────────────────
     The Dental Professionals and Contact forms stay native Shopify contact
     forms (no-JS fallback, emails the store). When data-dnt-api is set, the
     submit is intercepted and the same fields are POSTed as JSON to
     /api/wholesale on admin.trydenttastic.com (Origin = this store, CORS
     allowlisted there; no cookies), which writes the Postgres enquiry the
     /admin/pipeline inbox reads. Any failure other than a validation error
     falls back to the native submit so nothing is lost. */
  function apiForms() {
    document.querySelectorAll('form[data-dnt-api-form]').forEach(function (form) {
      var api = form.getAttribute('data-dnt-api') || '';
      if (!api) return;
      var kind = form.getAttribute('data-dnt-api-form');
      var done = form.parentElement.querySelector('[data-dnt-form-done]');
      var status = form.querySelector('[data-dnt-form-status]');
      var v = function (name) { var el = form.querySelector('[name="contact[' + name + ']"]'); return el ? String(el.value || '').trim() : ''; };
      form.addEventListener('submit', function (e) {
        if (form.getAttribute('data-native') === '1') return; // fallback path
        var body;
        if (kind === 'dental-pros') {
          var checked = form.querySelector('[name="contact[interested_in]"]:checked');
          body = { type: 'dental-pros', practiceName: v('practice_name'), contactName: v('name'), role: v('role') || 'Not stated', email: v('email'), phone: v('phone'), practiceAddress: v('practice_address'), enquiryType: (checked && checked.getAttribute('data-key')) || 'other', message: v('body') || undefined };
          var consent = form.querySelector('[name="contact[confirmation]"]');
          if (consent && !consent.checked) return; // let native validation speak
        } else if (kind === 'contact') {
          body = { type: 'contact', name: v('name') || 'Not given', email: v('email'), subject: v('subject') || 'Website contact', message: v('body') };
        } else return;
        e.preventDefault();
        var btn = form.querySelector('button[type=submit]'); if (btn) btn.disabled = true;
        if (status) status.hidden = true;
        fetch(api, { method: 'POST', mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
          .then(function (r) {
            if (r.status === 422) { return r.json().then(function () { throw { validation: true }; }); }
            if (!r.ok) throw new Error('api ' + r.status);
            form.hidden = true; if (done) done.hidden = false;
            if (window.gtag) try { window.gtag('event', kind === 'contact' ? 'contact_submit' : 'wholesale_enquiry_submit'); } catch (err) {}
          })
          .catch(function (err) {
            if (btn) btn.disabled = false;
            if (err && err.validation) { if (status) { status.textContent = (window.dntT || {}).needFields || 'Please check the highlighted fields.'; status.hidden = false; } return; }
            try { console.warn('[dnt] forms API unavailable, native submit', err); } catch (x) {}
            form.setAttribute('data-native', '1'); form.submit();
          });
      });
    });
  }
  function init() { reveal(); announce(); header(); accordions(); radios(); buybar(); reading(); floss(); legacyAnchor(); apiForms(); }
  var bound = false;
  function once() { if (bound) return; bound = true; capture(); revealPopup(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); once(); });
  else { init(); once(); }
  // Theme editor re-renders sections; re-run on section load.
  document.addEventListener('shopify:section:load', init);
})();
